/**
 * AssistantSection — section 2 of the homepage (Konzept § 5 Z. 127, 137).
 *
 * Job: micro-conversion, and the AI claim proven by answering rather than
 * asserted. This is the section that has to survive the page's own core question,
 * "Was kostet eine falsche Antwort?" — so its failure modes matter more than its
 * happy path.
 *
 * Three properties, in the order they constrain the design:
 *
 *  1. SSG-safe. The server renders a deterministic static teaser (greeting +
 *     example questions + input). No RPC, no browser API, no state that differs
 *     between server and client — the most prominent section on the page stays
 *     readable without JavaScript and agent-readable for crawlers (Konzept § 11
 *     Baustein 1). The conversation mounts only after the first interaction.
 *
 *  2. Graceful degradation. quaticoAssistentAdvise throws `assistent_unavailable`
 *     when the LLM call fails, and the rate limiter can refuse a burst. Either way
 *     the section shows a calm message plus a real exit (the paid Potenzialanalyse)
 *     — never a broken chat, always a way forward. Unlike the KI-Berater there is
 *     no second engine to fall back to, so the exit *is* the fallback.
 *
 *  3. No hardcoded user-facing text. Every string comes from MDX via the section
 *     content, including the fallback copy.
 */

import React from "react";
import { quaticoAssistentAdvise } from "@quaweb/service-proxies";
import { type SectionAssistantProps } from "../../content/section-assistant";
import { useContent } from "../../hooks";
import { trackCustomEvent } from "../../utils/tag-manager";
import { Button } from "../ui";
import { renderLucideIcon } from "../ui/RenderLucideIcon";

/**
 * The advice contract we rely on. The generated proxy does not re-export the
 * server's `RawAdvice` declaration — it resolves to `any` at the package boundary
 * — so we declare what we read locally rather than reaching into a server path.
 * Mirrors quatico-assistent/advice-schema.
 */
/** The engine's offer vocabulary — mirrors OFFER_KEYS in advice-schema.ts. */
type OfferKey =
    | "potenzialanalyse"
    | "ki-agenten-workshop"
    | "system-engineering"
    | "agentic-seo"
    | "feasibility"
    | "mission-analysis"
    | "go-to-market-test"
    | "product-scale-up"
    | "offerten-generator"
    | "document-as-a-service";

/** The engine's technology vocabulary — mirrors TECH_KEYS in advice-schema.ts. */
type TechKey = "magellan" | "webbloqs" | "appshell" | "websmith" | "agent-skills" | "plot" | "stack" | "dom-serializer";

type Advice = {
    phase: "asking" | "answering" | "result";
    nextQuestion?: { prompt: string };
    /** Auf einer Wissensantwort: wonach gefragt wurde — nur fürs Tracking. */
    detectedTopic?: "preis" | "dauer" | "praesenzzeit" | "aufwand";
    /**
     * Die Wissensantwort: ein Fakt plus die Einladung weiterzureden.
     *
     * `answer` und `sourceHref` sind server-gestempelt, `invitation` kommt vom
     * Modell. Bewusst OHNE Ergebnis-Karte gerendert: Eine Preisfrage ist eine
     * Wissensfrage — wer nach einer Zahl fragt und eine Verkaufskarte bekommt,
     * erlebt genau den Bruch, den die Ton-Regel verhindern soll.
     */
    answering?: { answer?: string; sourceHref?: string; invitation?: string; label?: string };
    /** Server-set on a contact escalation (route "kontakt") — the handover signal. */
    escalate?: boolean;
    recommendation?: {
        /** The recommended offer; the CTA label is keyed by this. */
        offer?: OfferKey;
        /** Set on a direct technology question (route "technologie") instead of `offer`. */
        tech?: TechKey;
        headline: string;
        rationale: string;
        example: string;
        /** Server-set: "angebot" → offer page, "kontakt" → escalation, "technologie" → tech page. */
        route?: "angebot" | "kontakt" | "technologie";
        /** Server-set exit target, stamped from the knowledge base. */
        href?: string;
        /** Server-set "Technik dahinter" chips on an offer result; absent when none relate. */
        relatedTech?: { name: string; href: string; label: string }[];
    };
};

/**
 * Eine Nachricht des Verlaufs, wie sie an den Server geht.
 *
 * `phase` ist neu und der Grund, warum dieser Typ überhaupt angefasst wird: Der
 * Server leitet die Rundenzahl jetzt selbst aus dem Verlauf ab, statt sie vom
 * Client zu übernehmen. Ohne die Phase je Assistenten-Turn könnte er
 * Rückfragen nicht von Wissensantworten unterscheiden — und der Entscheid
 * «`answering` zählt nicht gegen das Rundenlimit» wäre wirkungslos.
 */
type AdvisorMessage = { role: "user" | "assistant"; content: string; phase?: Advice["phase"] };

/**
 * Ein sichtbarer Turn im Chatfenster.
 *
 * `answer` und `sourceHref` tragen nur Wissensantworten: Die Antwort steht
 * gesetzt über der Einladung (`text`), der Quellenlink dezent daneben. Ein
 * Turn ohne diese Felder rendert wie bisher — eine schlichte Bubble.
 */
type ChatTurn = { who: "user" | "bot"; text: string; answer?: string; sourceHref?: string; label?: string };

/**
 * Escalation target when the engine sends the visitor to human contact
 * (route "kontakt" → no offer href). Offer recommendations carry their own
 * `href` from the knowledge base, so this only covers the contact route.
 */
const CONTACT_HREF = "/angebote/ki-potenzialanalyse";

/**
 * Die Gestalt der beiden Auswege unter der Empfehlung.
 *
 * Bewusst identisch für «Antwort anpassen» und «Neu anfangen»: Sie sind
 * gleichrangige Angebote, die sich nur in der Reichweite unterscheiden. Wäre
 * einer betonter, läse er sich als der empfohlene Weg — und die Wahl gehört
 * dem Besucher, nicht uns.
 */
const AUSWEG_KLASSEN = [
    "text-sm font-semibold text-gray-500 underline transition hover:text-gray-900",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
].join(" ");

/**
 * `nested` seit dem 16.9.2026: Die Sektion steht jetzt im Kapitel «Die
 * Plattform» und bringt dort weder eigenen Hintergrund noch eine zweite
 * `h2` mit — dieselbe Schaltung wie in `PlattformNutzenSection`.
 */
export const AssistantSection: React.FC<SectionAssistantProps & { nested?: boolean }> = ({
    title,
    persona,
    greeting,
    inputPlaceholder,
    placeholders,
    offerCtas,
    techCtaLabel,
    sourceLabel,
    exampleQuestions,
    submitLabel,
    nested = false,
    restartLabel,
    stepBackLabel,
    fallback,
}) => {
    const Heading = nested ? "h3" : "h2";
    const { language } = useContent();
    const [turns, setTurns] = React.useState<ChatTurn[]>([]);
    const [messages, setMessages] = React.useState<AdvisorMessage[]>([]);
    const [advice, setAdvice] = React.useState<Advice | null>(null);
    const [draft, setDraft] = React.useState("");
    const [pending, setPending] = React.useState(false);
    const [failed, setFailed] = React.useState(false);

    // "Started" is what separates the server-rendered teaser from the live
    // conversation. It can only become true through a user gesture, so the first
    // client render matches the server exactly — no hydration mismatch.
    const started = turns.length > 0 || pending;

    /**
     * Der Begrüssungs-Auftritt startet bei Sichtkontakt (Jan, 11.8.2026).
     *
     * Der Assistent liegt unterhalb der Falz. Liefe die Animation beim
     * Seitenaufbau, wäre sie vorbei, bevor der Besucher hinscrollt — der Effekt
     * liefe für niemanden.
     *
     * **Der Observer schaltet nur den EFFEKT frei, nie den Inhalt.** Genau hier
     * lag der Fehler, den `WorkshopMethodsSection` dokumentiert: «Diese Seiten
     * werden statisch vorgerendert — dort läuft kein Observer, und das Bild
     * fehlte im ausgelieferten HTML komplett.» Die Begrüssung steht deshalb
     * immer im Markup; ohne Observer fehlt ihr nur die Animation.
     *
     * `false` als Startwert ist zugleich der SSG-Zustand: Server und erster
     * Client-Render stimmen überein, der Effekt beginnt erst danach.
     */
    const greetingRef = React.useRef<HTMLDivElement>(null);
    const [greetingSichtbar, setGreetingSichtbar] = React.useState(false);

    React.useEffect(() => {
        const element = greetingRef.current;
        // `IntersectionObserver` fehlt im SSG-Renderer und in jsdom, und ein
        // ungeschützter Aufruf risse dort die ganze Sektion mit. Fehlt er,
        // bleibt es beim Ruhezustand: Text vollständig da, nur ohne Auftritt.
        if (!element || typeof IntersectionObserver === "undefined") {
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setGreetingSichtbar(true);
                    // Einmalig: Der Auftritt wiederholt sich nicht beim
                    // Zurückscrollen — sonst tippt der Assistent den Besucher
                    // bei jeder Begegnung erneut an.
                    observer.disconnect();
                }
            },
            // Erst wenn die Bubble wirklich im Blick ist, nicht schon am Rand.
            { threshold: 0.6 }
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    /**
     * Zurück auf den Anfang (Jan, 9.8.2026).
     *
     * Ohne diesen Weg endet jedes Gespräch in einer Sackgasse: Die Eingabe
     * bleibt zwar bedienbar, aber eine neue Frage landet im ALTEN Verlauf. Der
     * Server leitet die Rundenzahl aus `messages` ab — nach dem Limit bekommt
     * jede weitere Frage die Kontakt-Eskalation statt einer Antwort. Wer ein
     * zweites Anliegen hat, konnte bisher nur die Seite neu laden.
     *
     * Setzt ALLE sechs Zustände zurück, nicht nur die sichtbaren: `messages`
     * ist der Gesprächsverlauf, den der Server bewertet — bliebe er stehen,
     * sähe der Besucher einen leeren Chat und bekäme trotzdem die Eskalation.
     */
    const restart = React.useCallback(() => {
        setTurns([]);
        setMessages([]);
        setAdvice(null);
        setDraft("");
        setFailed(false);
        setPending(false);
        trackCustomEvent("assistant_restart", { section: "homepage-assistant" });
    }, []);

    /**
     * Einen Schritt zurück (Befund 10.8.2026).
     *
     * Der Neustart darüber räumt alles weg. Richtig für ein zweites Anliegen —
     * zu teuer für eine Antwort, die danebenlag: Wer bei vier beantworteten
     * Fragen die letzte korrigieren will, verliert die drei richtigen mit. Die
     * Kosten der Korrektur richteten sich nach der Länge des Gesprächs statt
     * nach der Grösse des Fehlers.
     *
     * **Wie weit zurück, ist nicht konstant.** Ein `asking`- oder
     * `answering`-Turn hängt zwei Einträge an (Frage und Antwort); ein
     * `result` hängt NICHTS an — die Empfehlung lebt allein in `advice` und
     * wird als Karte gerendert. Steht also ein Ergebnis auf dem Schirm, ist der
     * letzte Zyklus halb: Die Nutzerantwort ging raus, die Reaktion darauf kam
     * nie im Verlauf an.
     *
     * Zurückgenommen wird deshalb bis einschliesslich der letzten NUTZER-
     * nachricht — die einzige Definition, die für beide Fälle stimmt. Eine
     * feste Zahl hätte im Ergebnis-Fall eine Runde zu viel gelöscht (im ersten
     * Entwurf genau so passiert: Der Chat war danach leer).
     *
     * Beide Listen werden gemeinsam gekürzt. `turns` ist das Sichtbare,
     * `messages` das, was der Server bewertet — er leitet die Rundenzahl daraus
     * ab. Liefe `messages` weiter, sähe der Besucher seine Korrekturchance,
     * verbrauchte aber trotzdem sein Frage-Budget: dieselbe Halbheit, gegen die
     * der Neustart gebaut wurde.
     *
     * `advice` fällt weg, damit die Ergebnis-Karte verschwindet; `failed` wird
     * NICHT angefasst — im Ausfall-Zustand gibt es keinen Verlauf, zu dem man
     * zurückkehren könnte, dort steht der Neustart.
     */
    /**
     * Gibt es überhaupt etwas, wohin zurück?
     *
     * Nur wenn VOR der letzten Nutzerfrage schon eine Runde lag. Beim ersten
     * Zyklus wäre ein Schritt zurück ein Neustart — und den gibt es daneben.
     *
     * Dieselbe Stelle wie in `stepBack`: Zeigte der Link etwas anderes an, als
     * der Klick tut, wäre er schlimmer als keiner.
     */
    const hatVorherigeRunde = turns.map(t => t.who).lastIndexOf("user") > 0;

    const stepBack = React.useCallback(() => {
        setTurns(prev => {
            const letzteFrage = prev.map(t => t.who).lastIndexOf("user");
            return letzteFrage < 0 ? prev : prev.slice(0, letzteFrage);
        });
        setMessages(prev => {
            const letzteFrage = prev.map(m => m.role).lastIndexOf("user");
            return letzteFrage < 0 ? prev : prev.slice(0, letzteFrage);
        });
        setAdvice(null);
        setDraft("");
        trackCustomEvent("assistant_step_back", { section: "homepage-assistant" });
    }, []);

    // Context-specific placeholder, chosen by conversation phase (Mat's pattern,
    // but content-driven): the field names the *next* expected input. Each phase
    // falls back to the flat `inputPlaceholder`, so the whole feature is optional.
    // Derived from `advice.phase` once a conversation exists, else the initial
    // teaser text — computed identically on server and first client render.
    const activePlaceholder =
        (advice?.phase === "result" ? placeholders?.result : started ? placeholders?.asking : placeholders?.initial) ??
        inputPlaceholder;

    const ask = React.useCallback(
        async (question: string) => {
            const trimmed = question.trim();
            if (!trimmed || pending) {
                return;
            }

            const history: AdvisorMessage[] = [...messages, { role: "user", content: trimmed }];
            const isFirstQuestion = messages.length === 0;

            setTurns(prev => [...prev, { who: "user", text: trimmed }]);
            setMessages(history);
            setDraft("");
            setPending(true);

            if (isFirstQuestion) {
                // The micro-conversion § 5 Z. 127 asks us to measure.
                trackCustomEvent("assistant_first_question", { section: "homepage-assistant" });
            }

            try {
                // Die Rundenzahl kommt NICHT mehr von hier. Der Server leitet
                // sie aus `messages` ab — die Phase je Turn macht das möglich.
                //
                // Zwei Gründe: Diese Zeile zählte alle Assistenten-Turns, womit
                // `answering` das Frage-Budget verbraucht hätte; und eine
                // Grenze, an der der Injektionsschutz hängt, gehört nicht in
                // den Client, der sie mit `roundCount: 0` unterlaufen könnte.
                const raw = (await quaticoAssistentAdvise({
                    messages: history,
                    language: language === "en" ? "en" : "de",
                })) as Advice;

                setAdvice(raw);
                if (raw.phase === "asking" && raw.nextQuestion) {
                    // Die Phase wandert in den Verlauf, damit der Server die
                    // Runden selbst zählen kann (siehe AdvisorMessage).
                    setMessages([...history, { role: "assistant", content: raw.nextQuestion.prompt, phase: "asking" }]);
                    setTurns(prev => [...prev, { who: "bot", text: raw.nextQuestion!.prompt }]);
                } else if (raw.phase === "answering" && raw.answering) {
                    // Antwort und Einladung sind EIN Turn — das ist der Kern der
                    // Phase: konkret antworten und zugleich das Gespräch öffnen.
                    // Der Verlauf trägt beides plus die Phase, damit der Server
                    // die Runde nicht als Rückfrage zählt.
                    const gesagt = [raw.answering.answer, raw.answering.invitation].filter(Boolean).join(" ");
                    setMessages([...history, { role: "assistant", content: gesagt, phase: "answering" }]);
                    setTurns(prev => [
                        ...prev,
                        {
                            who: "bot",
                            text: raw.answering!.invitation ?? "",
                            answer: raw.answering!.answer,
                            sourceHref: raw.answering!.sourceHref,
                            label: raw.answering!.label,
                        },
                    ]);
                    trackCustomEvent("assistant_answer_shown", {
                        section: "homepage-assistant",
                        topic: raw.detectedTopic ?? "unknown",
                    });
                } else if (raw.phase === "result" && raw.recommendation) {
                    // The conversion funnel splits here: a "kontakt" route is the
                    // honest escalation to a human (Sales handover), any other route
                    // is a real offer recommendation ("it worked"). Measuring both —
                    // plus the CTA click below — is what turns the assistant from a
                    // black box into something we can judge (Konzept § 8).
                    const isEscalation = raw.escalate === true || raw.recommendation.route === "kontakt";
                    if (isEscalation) {
                        trackCustomEvent("assistant_escalation", { section: "homepage-assistant" });
                    } else {
                        trackCustomEvent("assistant_recommendation_shown", {
                            section: "homepage-assistant",
                            offer: raw.recommendation.offer ?? "unknown",
                        });
                    }
                }
            } catch {
                // assistent_unavailable, a network failure, or a refused burst —
                // all resolve the same way: stop the chat, offer the exit.
                setFailed(true);
            } finally {
                setPending(false);
            }
        },
        [language, messages, pending]
    );

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        void ask(draft);
    };

    // The chat header (avatar + name + online status) is the single strongest
    // "this is a chatbot" signal. The avatar is chrome (a Lucide glyph); name and
    // status are copy from MDX. Rendered identically in the failed and live
    // branches so the window frame never changes shape between states.
    const personaName = persona?.name ?? title;
    const header = (
        <div className="flex items-center gap-3 border-b border-gray-100 bg-white px-5 py-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white">
                {renderLucideIcon("Bot", { size: 22, "aria-hidden": true })}
            </span>
            <div className="min-w-0">
                <p className="truncate font-bold text-gray-900">{personaName}</p>
                {persona?.status && (
                    <p className="flex items-center gap-1.5 text-sm text-gray-500">
                        <span className="inline-block h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
                        {persona.status}
                    </p>
                )}
            </div>
        </div>
    );

    // A bot message bubble with the avatar gutter — shared by the greeting and
    // every assistant turn so they line up as one voice.
    //
    // `tippend` zerlegt den Text in Zeichen-Spans, damit er sich aufbauen kann
    // (nur die Begrüssung nutzt das). Der Text bleibt dabei unverändert und
    // vollständig — `textContent` liefert denselben Satz wie ohne Zerlegung,
    // Auswahl und Vorlesen funktionieren wie zuvor.
    const botBubble = (text: string, key?: string, testid?: string, tippend = false) => (
        <div key={key} className="flex items-end gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white">
                {renderLucideIcon("Bot", { size: 15, "aria-hidden": true })}
            </span>
            <p
                data-testid={testid}
                className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white px-4 py-3 text-gray-900 shadow-sm"
            >
                {tippend
                    ? [...text].map((zeichen, index) => (
                          <span
                              // Der Index ist hier der richtige Schlüssel: Die
                              // Zeichenfolge ist statisch, es wird nichts
                              // umsortiert oder eingefügt.
                              key={index}
                              className="assistant-greeting-char"
                              // 800ms Vorlauf für die Punkte, dann 18ms je
                              // Zeichen: Ein 88-Zeichen-Satz steht nach rund
                              // 2.4s. Schnell genug, dass niemand wartet,
                              // langsam genug, dass man das Tippen sieht.
                              style={{ animationDelay: `${800 + index * 18}ms` }}
                          >
                              {zeichen}
                          </span>
                      ))
                    : text}
            </p>
        </div>
    );

    /**
     * Eine Wissensantwort: der Fakt, ein dezenter Quellenlink, die Einladung.
     *
     * Dieselbe Bubble-Form wie jede andere Antwort — bewusst **keine**
     * Ergebnis-Karte. Eine Preisfrage ist eine Wissensfrage; wer nach einer
     * Zahl fragt und eine Verkaufskarte bekommt, erlebt genau den Bruch, den
     * die Ton-Regel verhindern soll. Der Weg zur Quelle ist da und anklickbar,
     * er drängt sich nur nicht auf.
     */
    const answerBubble = (turn: ChatTurn, key: string) => (
        <div key={key} className="flex items-end gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white">
                {renderLucideIcon("Bot", { size: 15, "aria-hidden": true })}
            </span>
            <div
                data-testid="assistant-answer"
                className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white px-4 py-3 text-gray-900 shadow-sm"
            >
                {/* Was gemessen wird, wo die Zahl es nicht selbst sagt
                    (10.8.2026). «ab CHF 1'290» braucht nichts, «ab 4–6 Wochen»
                    schon: Kalenderzeit und Präsenzzeit sind beide gültige
                    Antworten auf «wie lange dauert das?». Server-gestempelt und
                    nur bei den zwei Zeitgrössen gesetzt — fehlt es, rendert die
                    Bubble wie zuvor. Klein und grau: Die Zahl bleibt die
                    Antwort, das Label ordnet sie nur ein. */}
                {turn.label && (
                    <p data-testid="assistant-answer-label" className="text-sm text-gray-500">
                        {turn.label}
                    </p>
                )}
                {/* Fakt und Rückfrage in EINEM Absatz — nicht als zwei Blöcke
                    mit dem Quellenlink dazwischen (Nutzerfeedback vom
                    13.8.2026). Ein Nutzer las die alte Anordnung als Schema:
                    «Diese Antwort hilft mir nur wenig. Und warum ist die Antwort
                    immer zwei geteilt? Das verstehe ich nicht.» Kritik an der
                    FORM ist selten — sie heisst, dass die Form vor dem Inhalt
                    wahrgenommen wird.

                    Die Zweiteilung im INHALT bleibt (Jans Vorgabe vom 7.8.2026,
                    zitiert in `advice-schema.ts`: «alle Informationen möglichst
                    konkret beantworten aber mit einem Versuch verknüpft, ein
                    Gespräch zu eröffnen»). Geändert ist allein, dass sie sich
                    liest wie ein Gedanke: Der Fakt behält sein Gewicht, die
                    Rückfrage schliesst im gleichen Zug an und tritt optisch
                    zurück, und der Beleg steht danach — dort, wo man nachliest,
                    statt mitten im Satz. */}
                <p className="font-medium">
                    {turn.answer}
                    {turn.text && <span className="font-normal text-gray-600"> {turn.text}</span>}
                </p>
                {turn.sourceHref && (
                    <a
                        href={turn.sourceHref}
                        className="mt-1 inline-block text-sm text-gray-500 underline hover:text-gray-900"
                    >
                        {sourceLabel ?? turn.sourceHref}
                    </a>
                )}
            </div>
        </div>
    );

    if (failed) {
        return (
            <section data-testid="assistant-section" className={nested ? "" : "bg-gray-50 py-24"}>
                <div className="mx-auto w-full px-4 md:px-12">
                    <div className="mx-auto max-w-2xl overflow-hidden rounded-3xl border border-gray-200 bg-gray-50 shadow-xl">
                        {header}
                        <div className="px-6 py-10 text-center">
                            <Heading className="mb-4 text-2xl font-black text-gray-900 md:text-3xl">
                                {fallback.title}
                            </Heading>
                            <p className="mb-8 text-lg leading-relaxed text-gray-600">{fallback.message}</p>
                            <Button href={fallback.ctaHref} variant="primary" size="lg">
                                {fallback.ctaText}
                            </Button>

                            {/* Auch hier zurück auf den Anfang (9.8.2026). Der
                                Ausfall-Zustand ist die härtere Sackgasse: Er ersetzt
                                das ganze Chatfenster, es gibt also nicht einmal mehr
                                eine Eingabe. War die Störung vorübergehend — eine
                                abgewiesene Anfragenspitze etwa —, kostet ein neuer
                                Versuch einen Klick statt eines Seitenneuladens. */}
                            {restartLabel && (
                                <div>
                                    <button
                                        type="button"
                                        data-testid="assistant-restart-failed"
                                        onClick={restart}
                                        className="mt-6 text-sm font-semibold text-gray-500 underline transition hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                    >
                                        {restartLabel}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section data-testid="assistant-section" className={nested ? "" : "bg-gray-50 py-24"}>
            <div className="mx-auto w-full px-4 md:px-12">
                <Heading className="mb-8 text-center text-2xl font-black text-gray-900 md:text-3xl">{title}</Heading>

                {/* The whole conversation lives inside a bounded chat window: a
                    bordered, shadowed card with a persona header, a scrolling
                    message area and a docked input. That containment — not any
                    single message — is what reads as "chatbot" rather than
                    "search box with suggestions". */}
                <div className="mx-auto max-w-2xl overflow-hidden rounded-3xl border border-gray-200 bg-gray-50 shadow-xl">
                    {header}

                    <div data-testid="assistant-chat" className="space-y-4 px-5 py-6">
                        {/* Static greeting: rendered server-side so the empty state
                            already looks like a live chat. No state → SSG-safe. */}
                        {/* Die Begrüssung tritt auf, wie eine echte Antwort
                            entsteht (11.8.2026): erst der Tipp-Indikator, dann
                            der Satz Zeichen für Zeichen.

                            Der Ruhezustand las sich wie ein Kontaktformular —
                            dass hier etwas ANTWORTET, musste der Besucher
                            glauben. Jetzt sieht er es.

                            Zwei Dinge sind daran nicht verhandelbar:

                            1. Der Text steht VOLLSTÄNDIG im Markup, zerlegt in
                               Zeichen-Spans. Crawlbar, ohne JavaScript lesbar,
                               identisch auf Server und erstem Client-Render.
                               Zeichen-Spans statt einer wandernden Maske, weil
                               die Begrüssung umbricht: Eine Maske über den
                               ganzen Block deckte alle Zeilen gleichzeitig auf
                               — ein Vorhang, keine Schreibmaschine.

                            2. Der Auftritt startet bei SICHTKONTAKT, nicht beim
                               Laden. Der Assistent liegt unterhalb der Falz;
                               ohne Observer wäre die Animation vorbei, bevor
                               jemand hinsieht (Jan, 11.8.2026).

                            Der Observer schaltet AUSSCHLIESSLICH den Effekt
                            frei, nie den Inhalt — die Lehre aus
                            WorkshopMethodsSection, wo genau das im SSG-HTML
                            zum fehlenden Bild führte. */}
                        {greeting && (
                            <div ref={greetingRef} className="relative">
                                <div className={greetingSichtbar ? "assistant-greeting-reveal" : undefined}>
                                    {botBubble(greeting, undefined, "assistant-greeting", greetingSichtbar)}
                                </div>
                                {/* Rein dekorativ: Ein Screenreader würde sonst
                                    eine zweite, leere Bubble ansagen — und die
                                    Begrüssung darunter steht ohnehin schon da. */}
                                <div
                                    data-testid="assistant-greeting-typing"
                                    aria-hidden="true"
                                    className={[
                                        "assistant-greeting-typing absolute inset-0 flex items-end gap-2",
                                        greetingSichtbar ? "assistant-typing-run" : "",
                                    ].join(" ")}
                                >
                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white">
                                        {renderLucideIcon("Bot", { size: 15, "aria-hidden": true })}
                                    </span>
                                    <span className="flex gap-1 rounded-2xl rounded-bl-sm bg-white px-4 py-4 shadow-sm">
                                        <span className="assistant-typing-dot h-2 w-2 rounded-full bg-gray-400" />
                                        <span className="assistant-typing-dot h-2 w-2 rounded-full bg-gray-400" />
                                        <span className="assistant-typing-dot h-2 w-2 rounded-full bg-gray-400" />
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* The live turns are the SSG boundary: this container is
                            absent until a user gesture, so its presence marks
                            "conversation started". The static greeting/teaser above
                            it stays server-rendered and crawlable. */}
                        {/* Die Live-Region gehört auf den Verlauf, nicht nur auf
                            den Tipp-Indikator: Sonst hören
                            Screenreader-Nutzer «lädt» — und dann nichts. Für
                            eine Wissensantwort wiegt das schwerer als für ein
                            Ergebnis, weil sie reiner Text ist, ohne CTA, den
                            Tab findet. */}
                        {started && (
                            <div
                                data-testid="assistant-conversation"
                                className="space-y-4"
                                aria-live="polite"
                                aria-atomic="false"
                            >
                                {turns.map((turn, index) =>
                                    turn.who === "user" ? (
                                        <p
                                            key={`user-${index}`}
                                            data-testid="assistant-turn-user"
                                            className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-gray-900 px-4 py-3 text-white"
                                        >
                                            {turn.text}
                                        </p>
                                    ) : turn.answer ? (
                                        answerBubble(turn, `answer-${index}`)
                                    ) : (
                                        botBubble(turn.text, `bot-${index}`, "assistant-turn-bot")
                                    )
                                )}
                            </div>
                        )}

                        {/* `role="status"` statt einer zweiten
                            `aria-live`-Region: Zwei konkurrierende Regionen
                            unterbrechen einander, und dann geht die Antwort
                            unter — ausgerechnet das, was der Nutzer hören will.
                            Der Tipp-Indikator ist Statusinformation, der
                            Verlauf ist Inhalt. */}
                        {pending && (
                            <div data-testid="assistant-pending" className="flex items-end gap-2" role="status">
                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white">
                                    {renderLucideIcon("Bot", { size: 15, "aria-hidden": true })}
                                </span>
                                <span className="flex gap-1 rounded-2xl rounded-bl-sm bg-white px-4 py-4 shadow-sm">
                                    <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
                                    <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
                                    <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" />
                                </span>
                            </div>
                        )}

                        {advice?.phase === "result" && advice.recommendation && (
                            <div
                                data-testid="assistant-result"
                                className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
                                // The CTA click is the actual conversion (Konzept § 8).
                                // Button owns its own onClick (navigation) and forwards no
                                // handler, so the event is captured here by delegation: a
                                // real click on the link — mouse OR keyboard Enter — fires a
                                // native, bubbling click event, and this handler only pushes
                                // to the dataLayer (synchronous, non-blocking), so navigation
                                // is unaffected. Anchored on the CTA target to avoid counting
                                // stray clicks elsewhere in the result card.
                                onClick={event => {
                                    // A tech chip and the tech CTA carry a `tech` property, never
                                    // `offer:"unknown"` — that would pollute the offer conversion
                                    // metric. Tech links resolve to a technology href; the primary
                                    // CTA resolves to the offer or (on a tech result) the tech href.
                                    const anchor = (event.target as HTMLElement).closest("a");
                                    if (!anchor) {
                                        return;
                                    }
                                    const rec = advice.recommendation;
                                    const relatedHrefs = new Set((rec?.relatedTech ?? []).map(t => t.href));
                                    const clickedHref = anchor.getAttribute("href") ?? "";
                                    if (rec?.tech || relatedHrefs.has(clickedHref)) {
                                        trackCustomEvent("assistant_cta_click", {
                                            section: "homepage-assistant",
                                            tech: rec?.tech ?? clickedHref,
                                        });
                                    } else {
                                        trackCustomEvent("assistant_cta_click", {
                                            section: "homepage-assistant",
                                            offer: rec?.offer ?? "unknown",
                                        });
                                    }
                                }}
                            >
                                <h3 className="mb-2 text-xl font-black text-gray-900">
                                    {advice.recommendation.headline}
                                </h3>
                                <p className="mb-4 text-gray-600">{advice.recommendation.rationale}</p>
                                {/* The label must name the *recommended* target, not the
                                    fixed fallback copy — otherwise it promises one thing
                                    while the href (stamped by the engine) points at
                                    another. A technology result (no offer, href on a
                                    /technologie page) uses the tech label; an offer uses
                                    its per-offer label; the contact route has no href, so
                                    it escalates to the Potenzialanalyse. */}
                                <Button href={advice.recommendation.href || CONTACT_HREF} variant="primary" size="md">
                                    {advice.recommendation.tech
                                        ? techCtaLabel ?? fallback.ctaText
                                        : (advice.recommendation.offer && offerCtas?.[advice.recommendation.offer]) ||
                                          fallback.ctaText}
                                </Button>

                                {/* "Technik dahinter": dezente Chips zu den verwandten
                                    Technologie-Seiten, gestempelt aus der Wissensbasis.
                                    Fehlt relatedTech → kein Block. Der externe
                                    dom-serializer-Chip öffnet via Button target=_blank. */}
                                {advice.recommendation.relatedTech && advice.recommendation.relatedTech.length > 0 && (
                                    <ul data-testid="assistant-related-tech" className="mt-4 flex flex-wrap gap-2">
                                        {advice.recommendation.relatedTech.map(tech => (
                                            <li key={tech.href}>
                                                <Button href={tech.href} variant="secondary" size="sm">
                                                    {tech.label}
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {/* Zurück auf den Anfang (Jan, 9.8.2026). Bewusst ein
                                    Textlink, kein Knopf: Die Empfehlung darüber ist die
                                    Conversion — ein zweiter Knopf daneben würde mit ihr
                                    konkurrieren. Wer neu anfangen will, sucht danach und
                                    findet ihn; wer nicht, wird nicht abgelenkt. */}
                                {/* Zwei Auswege, nach Kosten sortiert (10.8.2026):
                                    «Antwort anpassen» nimmt EINEN Zyklus zurück,
                                    «Neu anfangen» räumt alles. Bis dahin gab es nur
                                    das Zweite — jede Korrektur kostete den ganzen
                                    Kontext, unabhängig davon, wie klein der Fehler
                                    war.

                                    Beide bleiben Textlinks: Die Empfehlung darüber
                                    ist die Conversion, ein Knopf daneben würde mit
                                    ihr konkurrieren (Begründung vom 9.8.). Der
                                    Schritt zurück steht zuerst, weil er der
                                    kleinere Eingriff ist. */}
                                {(stepBackLabel && hatVorherigeRunde) || restartLabel ? (
                                    <div className="mt-4 flex flex-wrap items-center gap-4">
                                        {/* Nur ab dem ZWEITEN Zyklus: Beim ersten
                                            wäre ein Schritt zurück ein Neustart. */}
                                        {stepBackLabel && hatVorherigeRunde && (
                                            <button
                                                type="button"
                                                data-testid="assistant-step-back"
                                                onClick={stepBack}
                                                className={AUSWEG_KLASSEN}
                                            >
                                                {stepBackLabel}
                                            </button>
                                        )}
                                        {restartLabel && (
                                            <button
                                                type="button"
                                                data-testid="assistant-restart"
                                                onClick={restart}
                                                className={AUSWEG_KLASSEN}
                                            >
                                                {restartLabel}
                                            </button>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {/* Example questions ARE the static teaser: server-rendered,
                            crawlable, the cheapest way in. Now framed as suggestion
                            chips inside the window; they disappear once the
                            conversation owns the chat. */}
                        {!started && exampleQuestions.length > 0 && (
                            <ul
                                data-testid="assistant-examples"
                                className={[
                                    "flex flex-wrap gap-2 pl-9",
                                    // Die Chips folgen der Frage, statt ihr
                                    // vorauszueilen (11.8.2026): Man antwortet
                                    // nicht auf eine Frage, die noch nicht
                                    // gestellt ist. Ohne Lauf-Flag stehen sie
                                    // sofort da — das ist der SSG-Zustand.
                                    greetingSichtbar ? "assistant-examples-run" : "",
                                ].join(" ")}
                            >
                                {exampleQuestions.map(question => (
                                    <li key={question}>
                                        <button
                                            type="button"
                                            onClick={() => void ask(question)}
                                            className={[
                                                "rounded-full border border-gray-300 bg-white px-4 py-2",
                                                "text-sm text-gray-700 transition hover:border-gray-900 hover:text-gray-900",
                                            ].join(" ")}
                                        >
                                            {question}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Input docked at the bottom of the window with a send icon —
                        the last piece that turns "form" into "chat composer". */}
                    <form
                        onSubmit={handleSubmit}
                        className="flex items-center gap-3 border-t border-gray-100 bg-white px-4 py-4"
                    >
                        {/* The placeholder is the accessible name: repeating the section
                            heading here would tell a screen-reader user nothing about
                            the field, and placeholders alone are not announced. It also
                            shifts with the conversation state (see activePlaceholder), so
                            the field always names the *next* expected input. */}
                        <input
                            id="assistant-question"
                            type="text"
                            aria-label={activePlaceholder}
                            value={draft}
                            onChange={event => setDraft(event.target.value)}
                            placeholder={activePlaceholder}
                            disabled={pending}
                            className="flex-1 rounded-full border border-gray-300 px-6 py-4 text-lg text-gray-900 focus:border-gray-900 focus:outline-none"
                        />
                        <button
                            type="submit"
                            aria-label={submitLabel}
                            disabled={pending || !draft.trim()}
                            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white transition hover:scale-105 disabled:opacity-50"
                        >
                            {renderLucideIcon("ArrowUp", { size: 22, "aria-hidden": true })}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
};

export default AssistantSection;
